import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { FREE_PLAN } from '@/lib/free-plan'
import { FREE_DOMAIN_RULE, PAID_ANY_EMAIL } from '@/lib/free-domain'
import { WORK_EMAIL_FIX } from '@/lib/account-emails'

const POST_URL = 'https://uptrack.app/blog/free-uptime-monitoring-europe-gdpr'
const TITLE = 'Free Uptime Monitoring in Europe: Limits and GDPR'
const DESCRIPTION =
  'What free uptime monitoring includes in 2026, where your monitoring data is stored, and what to check for GDPR before you pick a free plan.'
const PUBLISHED = '2026-10-12'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is there free uptime monitoring that stores data in the EU?',
    a: 'Yes. Uptrack stores account and check data in PostgreSQL databases on servers in Nuremberg, Germany, and its free plan checks from Nuremberg. Any tool you consider should state its data location on a public page you can read before you sign up.',
  },
  {
    q: 'Does free uptime monitoring process personal data?',
    a: 'Usually a little. The account email address and the alert recipients are personal data. Monitored URLs can be too if they carry user names or IDs in the path or query. Keep personal data out of monitored URLs and headers.',
  },
  {
    q: 'Do I need a data processing agreement for a free plan?',
    a: 'If the tool processes personal data for you, GDPR Article 28 expects a contract that sets out the subject matter, duration, purpose, data types and your instructions. Ask the vendor whether a free plan gets one. This is general information, not legal advice.',
  },
  {
    q: 'What does the Uptrack free plan include?',
    a: `${FREE_PLAN.monitors} monitors checked every ${FREE_PLAN.intervalSeconds} seconds from one location (${FREE_PLAN.location}), HTTP and keyword checks, email plus one chat channel, ${FREE_PLAN.statusPages} status page, ${FREE_PLAN.teamMembers} team member and ${FREE_PLAN.historyDays} days of history. Commercial use is allowed. ${FREE_DOMAIN_RULE} ${PAID_ANY_EMAIL}`,
  },
  {
    q: 'What is the catch with a free plan?',
    a: 'Limits and rules. The number of free accounts is capped, so new sign-ups can see "Free plan is full". Free monitors can be paused after 37 days without a sign-in. Webhook, SMS and phone alerts are on Pro.',
  },
]

export const Route = createFileRoute('/_site/blog/free-uptime-monitoring-europe-gdpr')({
  component: FreeUptimeEuropePage,
  head: () => ({
    meta: [
      { title: `${TITLE} — Uptrack` },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: `${TITLE} — Uptrack` },
      {
        property: 'og:description',
        content: 'Free uptime monitoring compared on limits, locations and where the data lives, with a GDPR checklist for EU teams.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-free-uptime-monitoring-europe-gdpr.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: PUBLISHED },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
            logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' },
          },
          mainEntityOfPage: POST_URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'
const list = 'mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body'
const steps = 'mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body'
const h2 = 'font-sans mb-4 text-2xl font-semibold'

const FREE_PLANS: { tool: string; monitors: string; interval: string; where: string }[] = [
  { tool: 'Uptrack', monitors: '5', interval: '30 s', where: 'One location (Nuremberg)' },
  { tool: 'UptimeRobot', monitors: '50', interval: '5 min', where: 'Check the vendor' },
  { tool: 'StatusCake', monitors: '10', interval: '5 min', where: 'Check the vendor' },
  { tool: 'Better Stack', monitors: '10 plus 10 heartbeats', interval: '3 min', where: 'Check the vendor' },
  { tool: 'HetrixTools', monitors: '15', interval: '1 min', where: 'Check the vendor' },
]

const CHECKLIST: string[] = [
  'The data location is written on a public page, with the country, and you can read it before you sign up.',
  'The vendor lists its processors: payment, CDN, email, hosting. Each one says what data it sees.',
  'A transfer outside the EEA has a named mechanism, such as an adequacy decision or standard contractual clauses.',
  'A data processing agreement is available, and you know whether the free plan gets it.',
  'Deletion has a schedule: when your account is removed, when the backups go.',
  'The alert text that leaves for Slack, Discord or Telegram holds no personal data, because those messages sit with the chat provider.',
  'Your monitored URLs, headers and test accounts hold no personal data about real people.',
]

function FreeUptimeEuropePage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 12, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Free uptime monitoring in Europe: what you get, and where your data lives</h1>

      <section className="mb-12">
        <p className={p}>
          &quot;Free uptime monitoring&quot; means five different things: how many monitors, how often they run, from where, who may use the
          plan commercially, and which alert channels come with it. If you work in the EU there is a sixth: where does the monitoring data
          live, and who else touches it?
        </p>
        <p className={p}>
          This guide covers those six. It is not a ranking. For a ranked list of tools, read{' '}
          <Link to="/blog/best-free-uptime-monitoring-tools" className={link}>
            best free uptime monitoring tools
          </Link>
          . Here we explain what to read on a free plan, and what a European team should check about data before it points a tool at a
          production site. We make Uptrack, so we describe our own setup with its limits.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>What &quot;free&quot; really includes in 2026</h2>
        <p className={p}>
          Free plans differ more in what they limit than in what they offer. The numbers below come from each vendor&apos;s public pricing
          page, checked on October 12, 2026.
        </p>
        <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="Free plans compared">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Tool</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Free monitors</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Check interval</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">Locations</th>
              </tr>
            </thead>
            <tbody>
              {FREE_PLANS.map((row) => (
                <tr key={row.tool} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.tool}</td>
                  <td className="py-3 px-3 num text-body">{row.monitors}</td>
                  <td className="py-3 px-3 num text-body">{row.interval}</td>
                  <td className="py-3 pl-3 text-body">{row.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={p}>
          We left out the location of the other tools because their pricing pages do not say, and we do not guess. Read their docs. Three
          more limits hide below the headline numbers:
        </p>
        <ul className={list}>
          <li>
            <strong>Commercial use.</strong> Free plans have changed their terms before. Reports about UptimeRobot&apos;s free plan differ:
            some say it was limited to personal use from late 2024, newer ones say the 2026 terms allow commercial use. Read the current
            terms page of any tool you use for a business site. Ours allows commercial use.
          </li>
          <li>
            <strong>Alert channels.</strong> Email is almost always free. Chat, SMS and phone are where plans differ. Ours has email plus one
            chat channel (Discord, Slack or Telegram). Webhook, SMS and phone alerts are on Pro.
          </li>
          <li>
            <strong>Activity rules.</strong> Some services pause idle free accounts. Ours can pause free monitors after 37 days without a
            sign-in, and the number of free accounts is capped, so sign-ups can show &quot;Free plan is full&quot;.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Why a monitoring tool touches personal data at all</h2>
        <p className={p}>
          A monitor stores a URL, a status code, a response time and an error message. That sounds harmless. Three things make it less
          so. The account holds an email address and a name. Alert recipients are people. And a URL can contain personal data: a path such
          as <code>/users/anna.meier/export</code>, a query with an order number, a token in a header.
        </p>
        <p className={p}>
          If a vendor stores these for you, GDPR describes the vendor as a processor and you as the controller. Article 28 then expects a
          written contract with the subject matter, duration, purpose, types of data and your instructions, and it limits the processor
          from engaging another processor without your authorisation. The text is on{' '}
          <a className={link} href="https://gdpr-info.eu/art-28-gdpr/" rel="noopener noreferrer">
            gdpr-info.eu
          </a>
          , and the European Data Protection Board explains the roles in its{' '}
          <a
            className={link}
            href="https://www.edpb.europa.eu/system/files/documents/2023-10/EDPB_guidelines_202007_controllerprocessor_final_en.pdf"
            rel="noopener noreferrer"
          >
            guidelines 07/2020
          </a>
          . This is general information, not legal advice. Your data protection officer has the final word.
        </p>
        <p className={p}>The simplest protection costs nothing: keep personal data out of monitored URLs and headers, and use a dedicated test account.</p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Where Uptrack stores your data</h2>
        <p className={p}>
          Our{' '}
          <Link to="/privacy" className={link}>
            privacy page
          </Link>{' '}
          says it in these words: all data is stored in PostgreSQL databases on Netcup servers in Nuremberg, Germany. We do not use US-based
          database providers, and backups are encrypted and stored on a separate server. The free plan checks from one location,
          Nuremberg, so check traffic stays there too.
        </p>
        <p className={p}>The same page lists what else touches data, and it is short:</p>
        <ul className={list}>
          <li>Paddle takes payments. It handles card data directly, and we never see it. A free account has no payment at all.</li>
          <li>Cloudflare serves the public website as a CDN with DDoS protection, so ordinary web traffic data such as the IP address and headers passes through it.</li>
          <li>Alert and password emails come from our own mail server, not from a third-party email provider.</li>
          <li>We do not use Google Analytics or ad-tracking pixels, and the only cookie is the session cookie.</li>
        </ul>
        <p className={p}>
          Retention depends on the plan. A free account shows {FREE_PLAN.historyDays} days of history. When you delete an account, it is
          deactivated at once and can be restored for 30 days. After that we delete your account, monitors, incidents and check results,
          and encrypted backups expire within 35 more days.
        </p>
        <p className={p}>
          Two honest limits. First, a Cloudflare layer in front of a website means that not every request path stays inside the EU. Read
          the privacy page and judge it for your case. Second, this site has no data processing agreement page today. If your company needs
          a signed one, ask before you rely on the free plan, using the contact address on the privacy page.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>The Uptrack free plan, exactly</h2>
        <ul className={list}>
          <li>
            {FREE_PLAN.monitors} monitors, checked every {FREE_PLAN.intervalSeconds} seconds from one location ({FREE_PLAN.location}).
          </li>
          <li>HTTP and keyword monitors. Ping, port, DNS, SSL, heartbeat and browser checks are on paid plans.</li>
          <li>
            Email plus {FREE_PLAN.chatChannels} chat channel, {FREE_PLAN.statusPages} status page, {FREE_PLAN.teamMembers} team member,{' '}
            {FREE_PLAN.historyDays} days of history. Commercial use is allowed.
          </li>
          <li>
            {FREE_DOMAIN_RULE} {PAID_ANY_EMAIL} Sign-up takes any address. {WORK_EMAIL_FIX}
          </li>
        </ul>
        <p className={p}>
          There is no trial, and no credit card is needed for the free plan. The 30-day money-back guarantee covers paid plans.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>A checklist for choosing a free plan in the EU</h2>
        <ol className={steps}>
          {CHECKLIST.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ol>
        <p className={p}>
          If a vendor fails the first two, stop there. If it passes all seven, you still have to read the current terms, because plans
          change.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>When Uptrack&apos;s free plan is not for you</h2>
        <ul className={list}>
          <li>
            You need many free monitors. UptimeRobot lists 50 and HetrixTools 15. Ours has {FREE_PLAN.monitors}.
          </li>
          <li>You need checks from several locations for free. Free checks run from Nuremberg only.</li>
          <li>You need a signed data processing agreement before the first monitor. Talk to us first, or choose a vendor that publishes one.</li>
          <li>You need SMS or phone alerts without paying. They are on Pro.</li>
          <li>
            You can only sign up with an address on a free mail provider. {FREE_DOMAIN_RULE} {PAID_ANY_EMAIL}
          </li>
        </ul>
      </section>

      <section className="mb-12 rounded-lg border border-border bg-muted p-5" lang="de">
        <h2 className="font-sans mb-3 text-xl font-semibold">Kostenloses Uptime-Monitoring</h2>
        <p className="text-lg leading-[1.7] text-body">
          Die kostenlose Version von Uptrack prüft bis zu 5 Monitore alle 30 Sekunden von einem Standort in Nürnberg aus. Konto- und
          Prüfdaten liegen in Datenbanken auf Servern in Nürnberg, Deutschland. Die kostenlose Version braucht eine geschäftliche
          E-Mail-Adresse, bezahlte Tarife akzeptieren jede Adresse. Prüfen Sie bei jedem Anbieter, wo die Daten liegen, welche
          Unterauftragsverarbeiter beteiligt sind und ob ein Auftragsverarbeitungsvertrag (AVV) verfügbar ist.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>FAQ</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 text-lg font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Free monitoring with EU data</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">Five monitors, checked every 30 seconds from Nuremberg.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
            <Link to="/pricing">See plans</Link>
          </Button>
        </div>
        <PlanFinePrint className="mt-4" />
      </div>
    </article>
  )
}
